import React from "react"
import { useDispatch } from "react-redux"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { NAVIGATE_TO_OTHER_USER_PROFILE_PAGE } from "constants/events"
import { useAppSelector } from "appRedux/hooks"
import { updateSelectedTabIndex } from "appRedux/slices/profileTabsSlice"
import Avatar from "components/atoms/Avatar"

type UserCardProps = {
  id: number;
  handle: string;
  name: string;
  avatar: File | string | null;
  avatarWidth?: string;
  label?: string;
  badge?: React.ReactNode | null;
  skipDefaultNavigation?: boolean;
  onClick?: () => void;
}

export default function UserCard(props: UserCardProps) {
  const {
    id,
    handle,
    name = "",
    avatar,
    avatarWidth,
    badge = null,
    skipDefaultNavigation = false,
    label,
    onClick = new Function()
  } = props

  const selectedTabIndex = useAppSelector((state) => state.profileTabs.selectedTabIndex)

  const dispatch = useDispatch()

  function handleOnCardClicked() {
    resetSelectedProfileTabIndexForTargetUser(handle)
    if (!skipDefaultNavigation) {
      navigateToUserProfile(handle, id)
    }
    onClick()
  }

  function resetSelectedProfileTabIndexForTargetUser(handle: string) {
    if (!selectedTabIndex.hasOwnProperty(handle)) return
    dispatch(updateSelectedTabIndex({ handle, index: 0 }))
  }

  function navigateToUserProfile(handle: string, id: number) {
    const eventDetail = {
      id: id,
      handle: handle
    }
    document.dispatchEvent(new CustomEvent(NAVIGATE_TO_OTHER_USER_PROFILE_PAGE, { detail: eventDetail }))
  }

  return (
    <Wrapper className="user-card" onClick={handleOnCardClicked}>
      <Avatar
        handle={handle}
        imageSrc={avatar}
        width={avatarWidth}
        badge={badge}
      />
      <Stack>
        {label ? <Username>{label}</Username> : <>
          <Username>{handle}</Username>
          <Name>{name}</Name>
        </>}
      </Stack>
    </Wrapper>
  )
}

const Wrapper = styled(Stack)(({ theme }) => ({
  width: "fit-content",
  flexDirection: "row",
  alignItems: "center",
  gap: "8px",
  position: "relative",
  cursor: "pointer"
}))

const Username = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: "1.3rem"
}))

const Name = styled(Typography)(({ theme }) => ({
  fontWeight: 300,
  fontSize: "1.1rem",
  color: theme.palette.gray["12"]
}))
